import { parseGameIdentity } from './admission';

export interface InjuryPlayer {
  source: string; team: string; player: string; playerKey?: string;
  pairing: 'paired' | 'unpaired' | 'ambiguous';
  status: 'out' | 'doubtful' | 'questionable' | 'probable' | 'available';
  statusRaw: string; reason: string; reportAt: string; statusSince: string;
}
export interface InjuryBlock {
  rev: number; reportAt: string; source: string;
  teams: Record<string, { filed: boolean; players: InjuryPlayer[] }>;
}
export interface InjuryView { block: InjuryBlock | null; receivedAt: number; invalid: boolean }
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;
const date = (v: unknown): v is string => text(v) && Number.isFinite(Date.parse(v));

/** #1255: provider rows are display evidence, never inferred execution identity.
 * Invalid injury data must not invalidate the sibling score record. */
export function parseInjuries(value: unknown, key: string): InjuryBlock | null {
  const identity = parseGameIdentity(key);
  if (!identity || !object(value) || !Number.isSafeInteger(value.rev) || Number(value.rev) < 0
    || !date(value.reportAt) || !text(value.source) || !object(value.teams)) return null;
  const teams: InjuryBlock['teams'] = {};
  const entries = Object.entries(value.teams);
  if (entries.length !== 2) return null;
  for (const [team, raw] of entries) {
    if (!identity.teams.includes(team) || !object(raw) || typeof raw.filed !== 'boolean'
      || !Array.isArray(raw.players) || raw.players.length > 100 || (!raw.filed && raw.players.length)) return null;
    const players: InjuryPlayer[] = [];
    for (const row of raw.players) {
      if (!object(row) || row.team !== team || !text(row.player) || !text(row.source)
        || !['paired', 'unpaired', 'ambiguous'].includes(String(row.pairing))
        || (row.pairing === 'paired' ? !text(row.playerKey) : row.playerKey !== undefined)
        || !['out', 'doubtful', 'questionable', 'probable', 'available'].includes(String(row.status))
        || !text(row.statusRaw) || typeof row.reason !== 'string' || !date(row.reportAt) || !date(row.statusSince)
        || Date.parse(row.statusSince) > Date.parse(row.reportAt) || Date.parse(row.reportAt) > Date.parse(value.reportAt)) return null;
      players.push({ source: row.source, team, player: row.player, pairing: row.pairing as InjuryPlayer['pairing'],
        ...(row.pairing === 'paired' ? { playerKey: row.playerKey as string } : {}),
        status: row.status as InjuryPlayer['status'], statusRaw: row.statusRaw, reason: row.reason,
        reportAt: row.reportAt, statusSince: row.statusSince });
    }
    teams[team] = { filed: raw.filed, players };
  }
  return { rev: value.rev as number, reportAt: value.reportAt, source: value.source, teams };
}

/** Shares the score transport's admitted connection and snapshot boundaries.
 * Revisions belong to injuries, not scores. REST refreshes publication time
 * and supplies authoritative nulls until the producer emits live retirements. */
export class InjuryAdmission {
  private rows = new Map<string, InjuryView>();
  private versions = new Map<string, number>();
  private pending: { generation: number; rows: Map<string, InjuryView>; live: Set<string>; keys: Set<string> | null } | null = null;
  snapshot(): ReadonlyMap<string, InjuryView> { return new Map(this.rows); }
  reset(): void { this.rows.clear(); this.versions.clear(); this.pending = null; }
  disconnect(): void { this.pending = null; }
  begin(generation: number, keys: readonly string[] | null): void {
    this.pending = { generation, rows: new Map(), live: new Set(), keys: keys ? new Set(keys) : null };
  }
  private value(key: string, raw: unknown, now: number, previous?: InjuryView): InjuryView {
    if (raw === null) return { block: null, invalid: false, receivedAt: now };
    const block = parseInjuries(raw, key);
    if (!block) return { block: previous?.block ?? null, invalid: true, receivedAt: previous?.receivedAt ?? now };
    if (previous?.block && (block.rev < previous.block.rev
      || Date.parse(block.reportAt) < Date.parse(previous.block.reportAt))) return previous;
    return { block, invalid: false, receivedAt: now };
  }
  accept(key: string, raw: unknown, now: number, generation?: unknown): boolean {
    if (!parseGameIdentity(key)) return false;
    if (generation !== undefined) {
      if (!this.pending || generation !== this.pending.generation || (this.pending.keys && !this.pending.keys.has(key))) return false;
      this.pending.rows.set(key, this.value(key, raw, now, this.rows.get(key)));
    } else {
      this.rows.set(key, this.value(key, raw, now, this.rows.get(key)));
      this.versions.set(key, (this.versions.get(key) ?? 0) + 1);
      this.pending?.live.add(key);
    }
    return true;
  }
  end(scoreKeys: ReadonlySet<string>, now: number): void {
    const pending = this.pending;
    if (!pending) return;
    for (const key of new Set([...this.rows.keys(), ...scoreKeys])) {
      if (pending.keys && !pending.keys.has(key)) continue;
      if (pending.live.has(key) && scoreKeys.has(key)) continue;
      this.rows.set(key, scoreKeys.has(key) ? pending.rows.get(key) ?? this.value(key, null, now) : this.value(key, null, now));
      this.versions.set(key, (this.versions.get(key) ?? 0) + 1);
    }
    this.pending = null;
  }
  ticket(keys: readonly string[]): ReadonlyMap<string, number> {
    return new Map(keys.map(key => [key, this.versions.get(key) ?? 0]));
  }
  rest(key: string, raw: unknown, now: number, ticket: ReadonlyMap<string, number>): void {
    if (this.pending || ticket.get(key) !== (this.versions.get(key) ?? 0)) return;
    this.accept(key, raw, now);
  }
}
